import { z } from 'zod';
export * from './help.js';
/**
 * @beam/api-types — the typed HTTP contract both repos import (master spec §12/Q8:
 * BEAM owns the API surface). Published to GitHub Packages in WO-F4; consuming it in
 * beam-mobile is what unblocks the mobile track. SmartBox ships no HTTP layer — BEAM
 * defines this.
 *
 * Foundation status: only cross-cutting envelopes + auth + health are defined here.
 * The SmartBox-surface request/response schemas (ingest / confirm / discard /
 * correctFact / assistant.ask / estimator.* / provenance.chainFor) are authored in
 * WO-F4 once the engine is wired — they are intentionally absent so nothing implies a
 * shape the backend can't yet honor.
 */
// --- Standard error envelope (every endpoint) ---
export const ApiError = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
// --- Roles (master spec §4) ---
export const Role = z.enum(['owner', 'office', 'staff', 'consultant', 'subcontractor']);
// --- Auth ---
// W0 — `client` selects token delivery: 'web' → refresh in an httpOnly cookie (+ csrfToken in body);
// 'ios'/'android' (or omitted) → refresh token returned in the body. Stored in expo-secure-store on mobile.
export const AuthClient = z.enum(['web', 'ios', 'android']);
export const LoginRequest = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    client: AuthClient.optional(),
});
export const LoginResponse = z.object({
    token: z.string(),
    expiresInSeconds: z.number().int().positive(),
    // Present for mobile clients (refresh token in body); omitted for web (delivered as an httpOnly cookie).
    refreshToken: z.string().optional(),
    refreshExpiresInSeconds: z.number().int().positive().optional(),
    sessionId: z.string().uuid().optional(),
    // Present for web (double-submit CSRF token to echo in the X-CSRF-Token header on /refresh + /logout).
    csrfToken: z.string().optional(),
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        fullName: z.string(),
        role: Role,
        organizationId: z.string().uuid(),
    }),
});
// In-app subscriptions (2026-06-19) — POST /api/public/signup: create account first, then subscribe.
// The signup RESPONSE is identical to LoginResponse (the app is signed in immediately), so it is an alias.
export const SignupRequest = z.object({
    business_name: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(8),
});
export const SignupResponse = LoginResponse;
// POST /api/auth/refresh — web sends nothing (cookie + X-CSRF-Token header); mobile sends { refreshToken }.
export const RefreshRequest = z.object({ refreshToken: z.string().optional() });
// GET /api/auth/sessions → active sessions for the authenticated user (account-security screen).
export const SessionDto = z.object({
    id: z.string().uuid(),
    client: AuthClient,
    user_agent: z.string().nullable(),
    created_at: z.string(),
    last_used_at: z.string(),
});
// W3 — POST /api/auth/sse-ticket → short-lived single-purpose ticket for the EventSource URL
// (`/api/events?ticket=…`), so the full access token never lands in a query string / proxy log.
export const SseTicketResponse = z.object({ ticket: z.string() });
// --- Push (W4) ---
// GET /api/push/vapid-key → the server's VAPID public key for Web Push subscription. `configured`
// is false (and `key` null) when VAPID isn't set up server-side, so clients can skip registration.
export const VapidKeyResponse = z.object({
    configured: z.boolean(),
    key: z.string().nullable(),
});
export const PushPlatform = z.enum(['web', 'ios', 'android']);
// POST /api/push/subscribe — register a push target. Web sends the browser PushSubscription
// (endpoint + p256dh + auth, flattened); mobile sends an Expo push token (endpoint = the token,
// keys omitted). `platform` defaults to 'web' server-side when absent.
export const PushSubscribeRequest = z.object({
    platform: PushPlatform.optional(),
    endpoint: z.string(),
    p256dh: z.string().nullable().optional(),
    auth: z.string().nullable().optional(),
});
// --- Platform admin (customer-service surface) ---
// Separate token family from tenant auth: admin tokens carry aud:'platform' + no org. The web console
// holds the access token in memory and the refresh token in the httpOnly beam_admin_refresh cookie.
export const AdminLoginRequest = z.object({ email: z.string().email(), password: z.string().min(1) });
export const AdminLoginStage = z.enum(['mfa_required', 'enroll_required', 'authenticated']);
export const AdminLoginResponse = z.object({
    stage: AdminLoginStage,
    // Access token. For 'mfa_required'/'enroll_required' it is a pre-MFA token (mfa:false — only
    // /verify-mfa + /enroll accept it). For 'authenticated' (trusted device skipped TOTP) it is a full token.
    token: z.string(),
    expiresInSeconds: z.number().int().positive(),
    csrfToken: z.string(),
    admin: z.object({ id: z.string().uuid(), email: z.string().email(), fullName: z.string() }),
});
export const AdminTokenResponse = z.object({ token: z.string(), expiresInSeconds: z.number().int().positive() });
export const AdminVerifyMfaRequest = z.object({ code: z.string().min(1) });
// One-time enrollment payload — the ONLY place the raw secret + recovery codes are returned.
export const AdminMfaEnrollResponse = z.object({
    otpauthUri: z.string(),
    secret: z.string(),
    recoveryCodes: z.array(z.string()),
});
export const AdminMeResponse = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    fullName: z.string(),
    mfaEnrolled: z.boolean(),
});
export const OrgSummary = z.object({
    organizationId: z.string().uuid(),
    legalName: z.string(),
    ownerEmail: z.string().nullable(),
    licenseId: z.string().uuid().nullable(),
    status: z.string().nullable(),
    plan: z.string().nullable(),
    seats: z.number().int().nullable(),
    isComped: z.boolean(),
    currentPeriodEnd: z.string().nullable(),
    createdAt: z.string(),
});
export const OrgListResponse = z.object({ orgs: z.array(OrgSummary), total: z.number().int() });
export const OrgDetail = OrgSummary.extend({
    scopeValue: z.string().nullable(),
    stripeSubscriptionId: z.string().nullable(),
    stripeCustomerId: z.string().nullable(),
    notes: z.string().nullable(),
    userCount: z.number().int(),
});
export const LicenseActionResponse = z.object({ licenseId: z.string().uuid(), status: z.string() });
export const IssueCompedRequest = z.object({
    ownerEmail: z.string().email(),
    ownerName: z.string().optional(),
    plan: z.string().optional(),
    seats: z.number().int().positive().optional(),
});
export const IssueCompedResponse = z.object({
    organizationId: z.string().uuid(),
    licenseId: z.string(),
    ownerUserId: z.string(),
    alreadyProvisioned: z.boolean(),
    acceptUrl: z.string().optional(),
});
export const CheckoutLinkRequest = z.object({ ownerEmail: z.string().email(), priceId: z.string().optional() });
export const CheckoutLinkResponse = z.object({ url: z.string() });
export const AdminAuditRow = z.object({
    id: z.string().uuid(),
    adminId: z.string().uuid().nullable(),
    action: z.string(),
    targetOrgId: z.string().uuid().nullable(),
    targetLicenseId: z.string().uuid().nullable(),
    ip: z.string().nullable(),
    metadata: z.unknown(),
    createdAt: z.string(),
});
export const HealthStatusEnum = z.enum(['ok', 'warn', 'critical', 'unknown']);
export const DashboardMonitor = z.object({
    monitor: z.string(),
    status: HealthStatusEnum,
    /** Human-friendly monitor name, e.g. "Database". */
    title: z.string(),
    /** Plain-English one-liner describing the current state. */
    summary: z.string(),
    detail: z.unknown(),
    at: z.string().nullable(),
});
export const DashboardStats = z.object({
    totalOrgs: z.number(),
    totalUsers: z.number(),
    activeLicenses: z.number(),
    trialing: z.number(),
    pastDue: z.number(),
    expiringSoon: z.number(),
    canceled: z.number(),
    comped: z.number(),
    paidActive: z.number(),
});
export const DashboardOrgRow = z.object({
    organizationId: z.string(),
    legalName: z.string(),
    ownerEmail: z.string().nullable(),
    status: z.string().nullable(),
    isComped: z.boolean(),
    createdAt: z.string(),
});
export const DashboardResponse = z.object({
    overall: HealthStatusEnum,
    generatedAt: z.string(),
    monitors: z.array(DashboardMonitor),
    stats: DashboardStats,
    recentOrgs: z.array(DashboardOrgRow),
});
// --- Health ---
export const HealthResponse = z.object({
    status: z.literal('ok'),
    service: z.string(),
    env: z.string(),
});
// --- Current user (GET /me) ---
export const MeResponse = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    fullName: z.string(),
    role: Role,
    organizationId: z.string().uuid(),
});
/**
 * --- SmartBox surface (WO-F4) ---
 *
 * Mirrors the engine data interfaces in
 * beam-backend/docs/smartbox/smartbox-01-api-reference.md §3 EXACTLY (field names,
 * optionality). Two HTTP-boundary translations are applied deliberately:
 *   - engine `Date` fields → `z.string()` (JSON has no Date; values are ISO strings).
 *   - engine `Buffer` file content → base64-in-JSON (the backend does NOT use multipart).
 * `scope` is never in a request body — it is resolved from the auth token. batchId /
 * fileId / factId / draftId are path params, not body fields.
 */
// TokenUsage
export const TokenUsage = z.object({
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    estimatedCostUsd: z.number().optional(),
});
// --- Ingest (base64-in-JSON file upload) ---
export const IngestFile = z.object({
    filename: z.string(),
    mimeType: z.string(),
    contentBase64: z.string(),
});
export const IngestRequest = z.object({
    files: z.array(IngestFile).min(1),
});
// --- Extraction / batch results ---
export const ExtractedRecord = z.object({
    category: z.string(),
    value: z.unknown(),
    sourceText: z.string().optional(),
    confidence: z.number(),
    fileId: z.string(),
    batchId: z.string(),
});
export const FileResult = z.object({
    fileId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    extractedRecords: z.array(ExtractedRecord),
    confidence: z.number(),
    status: z.enum(['pending', 'confirmed', 'discarded', 'failed']),
});
export const BatchResult = z.object({
    batchId: z.string(),
    fileResults: z.array(FileResult),
    pendingConfirmation: z.boolean(),
});
// --- Facts ---
export const FactRecord = z.object({
    id: z.string(),
    category: z.string(),
    value: z.unknown(),
    sourceText: z.string().optional(),
    confidence: z.number().optional(),
    scopeIdentifier: z.string(),
    createdAt: z.string(), // ISO string (engine: Date)
    supersededById: z.string().optional(),
    supersededAt: z.string().optional(), // ISO string (engine: Date)
    isActive: z.boolean(),
});
export const CommitResult = z.object({
    batchId: z.string(),
    fileId: z.string(),
    scopeIdentifier: z.string(),
    committedRecords: z.array(FactRecord),
    submitterId: z.string(),
    commitAt: z.string(), // ISO string (engine: Date)
});
// --- Confirm (batchId/fileId are path params) ---
export const ConfirmRequest = z.object({
    submitterId: z.string(),
});
// --- Correct fact (factId is a path param) ---
export const FactPatch = z.object({
    value: z.unknown().optional(),
    category: z.string().optional(),
});
export const CorrectFactRequest = z.object({
    patch: FactPatch,
    reason: z.string().min(1),
});
// --- Assistant ---
export const Turn = z.object({
    role: z.enum(['user', 'assistant']),
    content: z.string(),
});
export const AssistantAskRequest = z.object({
    tenancyUnitId: z.string(),
    question: z.string().min(1),
    history: z.array(Turn).default([]),
});
export const AssistantReply = z.object({
    content: z.string(),
    usage: TokenUsage.optional(),
});
// --- Estimator (draftId is a path param) ---
export const EstimatorTurnRequest = z.object({
    input: z.string().min(1),
});
export const EstimatorReply = z.object({
    content: z.string(),
    draftId: z.string(),
    usage: TokenUsage.optional(),
});
export const EstimatorActivateRequest = z.object({
    tenancyUnitId: z.string(),
});
export const NewDraftResponse = z.object({
    draftId: z.string(),
});
// --- Provenance ---
export const ProvenanceEvent = z.object({
    eventType: z.enum(['extracted', 'committed', 'corrected', 'superseded', 'ingested']),
    timestamp: z.string(), // ISO string (engine: Date)
    actor: z.string(),
    detail: z.record(z.unknown()),
});
export const ProvenanceChain = z.object({
    factId: z.string(),
    events: z.array(ProvenanceEvent),
});
/**
 * --- Billing surface (WO-M4): estimate/invoice read DTOs + Stripe payment link ---
 *
 * Mirrors the real columns on prisma/schema.prisma (Estimate / EstimateLineItem /
 * Invoice / InvoiceLineItem / Payment) and the output of estimating.ts / invoicing.ts.
 * HTTP-boundary conventions (consistent with the SmartBox surface above):
 *   - money/decimal columns are serialized as STRINGS (Prisma Decimal → string; the
 *     backend emits fixed-2 strings, e.g. "1234.56"). Never a JS number (precision).
 *   - Date columns are serialized as ISO STRINGS.
 *   - Nullable columns are `.nullable()`; absent-by-design fields are `.optional()`.
 * NO card data ever crosses this boundary (invariant 11) — Stripe refs/fees only.
 */
// --- Estimate line item (snapshot fields: description + unit_cost at write time) ---
export const EstimateLineItemDto = z.object({
    id: z.string().uuid(),
    sortOrder: z.number().int(),
    section: z.string().nullable(),
    lineType: z.enum(['material', 'labor', 'assembly', 'custom']),
    description: z.string(), // snapshot
    quantity: z.string(), // decimal-as-string
    unitCost: z.string(), // snapshot, decimal-as-string
    unitPrice: z.string().nullable(),
    lineTotal: z.string().nullable(),
    isOptional: z.boolean(),
});
// --- Estimate enums (mirror Appendix B) ---
export const EstimateStatus = z.enum(['draft', 'sent', 'accepted', 'rejected', 'expired', 'converted']);
export const ApprovalStatus = z.enum([
    'draft',
    'presented',
    'approved',
    'sent_for_signature',
    'signed',
    'declined',
    'expired',
]);
export const EstimateSummary = z.object({
    id: z.string().uuid(),
    customerId: z.string().uuid(),
    jobId: z.string().uuid().nullable(),
    status: z.string(), // EstimateStatus; kept open (z.string) to tolerate enum drift
    approvalStatus: z.string(), // ApprovalStatus
    subtotal: z.string().nullable(),
    taxAmount: z.string().nullable(),
    discountTotal: z.string().nullable(),
    total: z.string().nullable(),
    currency: z.string(),
    smartboxDraftId: z.string().nullable().optional(),
    issuedDate: z.string().nullable(),
    validUntil: z.string().nullable(),
    presentedAt: z.string().nullable(),
    approvedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const EstimateDetail = EstimateSummary.extend({
    markupPct: z.string().nullable(),
    taxRateId: z.string().uuid().nullable(),
    lineItems: z.array(EstimateLineItemDto),
});
// --- Invoice line item (snapshot: description + unit_price) ---
export const InvoiceLineItemDto = z.object({
    id: z.string().uuid(),
    sortOrder: z.number().int(),
    section: z.string().nullable(),
    lineType: z.enum(['material', 'labor', 'assembly', 'custom']),
    description: z.string(), // snapshot
    quantity: z.string(),
    unitPrice: z.string(), // snapshot
    lineTotal: z.string().nullable(),
});
// --- Payment (NO card data — Stripe refs + fee fields only, invariant 11) ---
export const PaymentSummary = z.object({
    id: z.string().uuid(),
    amount: z.string(),
    currency: z.string(),
    method: z.string(), // PaymentMethod (cash | check | card | ...)
    status: z.string(), // PaymentStatus
    receivedDate: z.string().nullable(),
    processor: z.string().nullable(), // 'stripe' | null
    processorPaymentIntentId: z.string().nullable().optional(),
    processorFee: z.string().nullable().optional(),
    applicationFee: z.string().nullable().optional(), // BEAM platform fee (Payment.platform_fee)
    netToOrg: z.string().nullable().optional(),
});
export const InvoiceSummary = z.object({
    id: z.string().uuid(),
    customerId: z.string().uuid(),
    jobId: z.string().uuid().nullable(),
    estimateId: z.string().uuid().nullable().optional(),
    invoiceNumber: z.string().nullable(),
    type: z.string(), // InvoiceType (standard | deposit | progress | final)
    status: z.string(), // InvoiceStatus (draft | sent | partial | paid | ...)
    subtotal: z.string().nullable(),
    taxAmount: z.string().nullable(),
    discountTotal: z.string().nullable(),
    total: z.string().nullable(),
    amountPaid: z.string().nullable(),
    balanceDue: z.string().nullable(),
    currency: z.string(),
    stripeInvoiceId: z.string().nullable().optional(),
    stripePaymentLinkUrl: z.string().nullable().optional(),
    issuedDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const InvoiceDetail = InvoiceSummary.extend({
    taxRateId: z.string().uuid().nullable().optional(),
    lineItems: z.array(InvoiceLineItemDto),
    payments: z.array(PaymentSummary),
});
// --- Time & Materials (spec 2): accrue labor + materials against a job, then bill from actuals ---
export const CreateTimeEntryRequest = z.object({
    worker_id: z.string().uuid(),
    hours: z.number().positive(),
    work_date: z.string().optional(), // ISO; defaults to now server-side
    labor_rate_id: z.string().uuid().nullable().optional(),
    job_phase_id: z.string().uuid().nullable().optional(),
    notes: z.string().nullable().optional(),
});
export const CreateManualExpenseRequest = z.object({
    description: z.string().min(1),
    total_amount: z.number().nonnegative(),
    transaction_date: z.string().nullable().optional(),
    expense_category_id: z.string().uuid().nullable().optional(),
    receipt_attachment_id: z.string().uuid().nullable().optional(),
});
// Mirrors the JobActuals returned by GET /jobs/:id/actuals (raw domain shape — snake/camel as-is).
export const JobActualsDto = z.object({
    labor: z.array(z.object({
        labor_rate_id: z.string().uuid().nullable(),
        rate_name: z.string(),
        hours: z.number(),
        bill_rate: z.number().nullable(),
        amount: z.number(),
    })),
    materials: z.array(z.object({
        expense_id: z.string().uuid(),
        description: z.string(),
        amount: z.number(),
    })),
    laborTotal: z.number(),
    materialTotal: z.number(),
    total: z.number(),
    unbilledTimeEntryCount: z.number().int(),
    unbilledExpenseCount: z.number().int(),
    hasUnratedLabor: z.boolean(),
});
export const CreateInvoiceFromActualsResponse = z.object({
    invoiceId: z.string().uuid(),
});
// Raw labor_rate rows (GET /labor-rates) — the T&M log-hours rate picker.
export const LaborRateDto = z.object({
    id: z.string().uuid(),
    name: z.string(),
    cost_rate: z.string().nullable(),
    bill_rate: z.string().nullable(),
});
// --- In-app "Pay online" link (per-tenant Stripe Connect, 2026-06-27) ---
// The charge is a DIRECT charge on the org's OWN connected Stripe account (no platform fee).
// 'created'       → a hosted Checkout URL was created on the connected account.
// 'not_connected' → the org has not connected Stripe (or charges aren't enabled). The UI points
//                   the owner to Account → Payments and offers manual payment recording instead.
// 'already_paid'  → the invoice has no outstanding balance.
export const PaymentLinkResponse = z.object({
    status: z.enum(['created', 'not_connected', 'already_paid']),
    invoiceId: z.string().uuid(),
    url: z.string().optional(),
});
// --- Per-tenant Stripe Connect + pay-by-invoice config (2026-06-27) ---
// Authed, owner-gated. Surfaces under /api/account/payments/*. No card data, no keys ever cross
// this boundary — only the connected account id + capability flags + the public pay-page config.
export const PayPageConfig = z.object({
    slug: z.string().nullable(),
    customDomain: z.string().nullable(),
    domainVerified: z.boolean(),
    enabled: z.boolean(),
    hostedUrl: z.string().nullable(),
});
export const ConnectStatus = z.object({
    configured: z.boolean(), // platform-level: is Connect available at all
    connected: z.boolean(),
    status: z.string().nullable(), // 'connected' | 'restricted' | null
    chargesEnabled: z.boolean(),
    payoutsEnabled: z.boolean(),
    accountId: z.string().nullable(),
    payPage: PayPageConfig,
});
export const ConnectBeginResponse = z.object({ url: z.string() });
export const PayPageConfigInput = z.object({
    slug: z.string().nullable().optional(),
    customDomain: z.string().nullable().optional(),
    enabled: z.boolean().optional(),
});
export const DomainVerifyResponse = z.object({ verified: z.boolean(), expected: z.string() });
// --- PUBLIC pay-by-invoice surface (no auth; org resolved from slug/host) ---
// WO-INV1 — the public pay page shows the tenant's brand: their uploaded logo (presigned GET URL,
// null when none) and accent color (always present; the default '#1f6feb' when unset). These are
// safe to expose publicly (a logo + a color) and add NO customer PII or invoice line items.
export const PublicOrgBrand = z.object({
    slug: z.string(),
    businessName: z.string(),
    currency: z.string(),
    enabled: z.boolean(),
    acceptsPayments: z.boolean(),
    logoUrl: z.string().nullable(),
    accentColor: z.string(),
});
export const PublicInvoiceSummary = z.object({
    invoiceNumber: z.string(),
    businessName: z.string(),
    amountDue: z.string(),
    currency: z.string(),
    status: z.enum(['open', 'paid']),
});
export const PublicCheckoutResponse = z.object({ checkoutUrl: z.string() });
// ===========================================================================
// WO-INV1 — Branded invoice templates + per-tenant branding (logo & accent)
// ===========================================================================
//
// Org-scoped, owner-gated self-service under /api/account/branding/*. The org picks ONE of three
// built-in templates, an accent color, whether to show its logo, and an optional footer note; it
// uploads a single logo image. There are NO custom user-uploaded templates (deliberately out of
// scope). The same shape drives the authed settings screen AND the rendered invoice PDF
// (GET /api/invoices/:id/pdf). The backend mirrors this validation locally (it does not import this
// package — see the api-types boundary), so keep the two definitions in agreement.
// The three built-in layouts (see workorders/WO-INV1-invoice-templates-preview.html).
export const InvoiceTemplate = z.enum(['classic', 'modern', 'minimal']);
const ACCENT_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
export const FOOTER_NOTE_MAX = 280;
// Full, resolved branding (defaulted on read). accentColor is always a 6-digit hex; footerNote is
// optional and length-capped.
export const OrgBranding = z.object({
    invoiceTemplate: InvoiceTemplate,
    accentColor: z.string().regex(ACCENT_COLOR_RE),
    showLogo: z.boolean(),
    footerNote: z.string().max(FOOTER_NOTE_MAX).optional(),
});
// Sensible zero-setup defaults — a brand-new org renders a clean classic invoice with a wordmark.
export const DEFAULT_ORG_BRANDING = {
    invoiceTemplate: 'classic',
    accentColor: '#1f6feb',
    showLogo: true,
};
// PATCH /api/account/branding — partial update. Every field optional; footerNote may be set to null
// or '' to clear it. accentColor is validated as #rrggbb.
export const UpdateBrandingRequest = z.object({
    invoiceTemplate: InvoiceTemplate.optional(),
    accentColor: z.string().regex(ACCENT_COLOR_RE).optional(),
    showLogo: z.boolean().optional(),
    footerNote: z.string().max(FOOTER_NOTE_MAX).nullable().optional(),
});
// GET /api/account/branding → the resolved branding + the logo (presigned GET URL, null when none).
export const BrandingResponse = z.object({
    branding: OrgBranding,
    logoUrl: z.string().nullable(),
    hasLogo: z.boolean(),
});
// POST /api/account/branding/logo — base64-in-JSON upload (consistent with the SmartBox/import
// surfaces; the backend does not use multipart). Only raster logos make sense in a PDF; the router
// additionally restricts mime to png/jpeg/svg.
export const UploadLogoRequest = z.object({
    contentBase64: z.string().min(1),
    mimeType: z.string().min(1),
    fileName: z.string().optional(),
});
/**
 * --- Voice & Smart Intake surface (WO-24) ---
 *
 * Edge STT is LOCKED: the device transcribes audio → TEXT on-device and posts the
 * TEXT here. The backend does NO audio transcription (the STT provider is a device
 * concern, Gate Q5). The backend's job is to route the transcript to the right
 * SmartBox call by intent (ingest / assistant / estimator) — see voiceRouter.ts.
 *
 * `VoiceIntent` mirrors the prisma `VoiceIntent` enum members EXACTLY
 * (ingest | assistant | estimator). `scope` is never in the request body — it is
 * resolved from the auth token (invariant 13). `audioAttachmentId` is an optional
 * reference to a stored audio blob; raw audio is NEVER sent to this endpoint.
 */
export const VoiceIntent = z.enum(['ingest', 'assistant', 'estimator']);
// Mirrors prisma VoiceStatus (captured | transcribed | routed | committed | discarded).
export const VoiceStatus = z.enum(['captured', 'transcribed', 'routed', 'committed', 'discarded']);
export const VoiceRouteRequest = z.object({
    transcript: z.string().min(1),
    intent: VoiceIntent,
    audioAttachmentId: z.string().optional(),
    // Estimator: continue an existing draft instead of starting a new one.
    draftId: z.string().optional(),
    // Assistant/estimator: the tenancy unit context; defaults to org scope when absent.
    tenancyUnitId: z.string().optional(),
});
export const VoiceRouteResponse = z.object({
    voiceCaptureId: z.string().uuid(),
    intent: VoiceIntent,
    status: VoiceStatus,
    // ingest → SmartBox batchId; persisted on voice_capture.smartbox_batch_ref.
    batchId: z.string().optional(),
    // estimator → the draft advanced/created.
    draftId: z.string().optional(),
    // assistant → the model's reply content.
    assistantReply: z.string().optional(),
});
// ===========================================================================
// WO-D2 — licensing, self-serve onboarding, account admin, historical import
// ===========================================================================
// Public self-serve checkout (POST /api/public/checkout)
export const CheckoutRequest = z.object({ email: z.string().email() });
export const CheckoutResponse = z.object({ url: z.string().url() });
// Owner set-password invite (POST /api/auth/accept-invite) → returns LoginResponse
export const AcceptInviteRequest = z.object({ token: z.string().min(1), password: z.string().min(8) });
// ---------------------------------------------------------------------------
// Self-service password reset + in-app change password (public + authed).
// ---------------------------------------------------------------------------
// POST /api/auth/forgot-password (PUBLIC) — request a reset email. The response is ALWAYS
// { ok: true } regardless of whether the email maps to an account (no user enumeration).
export const ForgotPasswordRequest = z.object({ email: z.string().email(), client: AuthClient.optional() });
export const ForgotPasswordResponse = z.object({ ok: z.literal(true) });
// POST /api/auth/reset-password (PUBLIC) — complete a reset with the emailed token. On success the
// user is signed in immediately, so the response is a LoginResponse (all prior sessions are revoked).
export const ResetPasswordRequest = z.object({ token: z.string().min(1), password: z.string().min(8), client: AuthClient.optional() });
export const ResetPasswordResponse = LoginResponse;
// POST /api/auth/change-password (AUTHED) — verify the current password, set a new one. All other
// sessions are revoked and a fresh session is returned (this device stays signed in) → LoginResponse.
export const ChangePasswordRequest = z.object({ currentPassword: z.string().min(1), newPassword: z.string().min(8), client: AuthClient.optional() });
export const ChangePasswordResponse = LoginResponse;
// License status (GET /api/license) — always reachable, even when locked.
// `incomplete` (in-app subscriptions, 2026-06-19) = account created, awaiting first purchase (locked).
export const LicenseStatusValue = z.enum(['trialing', 'active', 'past_due', 'canceled', 'unpaid', 'incomplete']);
// Billing platform for the license (in-app subscriptions). 'apple'/'google' = store IAP via RevenueCat.
export const LicensePlatform = z.enum(['stripe', 'apple', 'google', 'manual']);
// Where the client should route subscription management given the platform.
export const LicenseManage = z.enum(['stripe_portal', 'app_store', 'play_store', 'none']);
export const LicenseStatusResponse = z.object({
    status: LicenseStatusValue.nullable(),
    active: z.boolean(),
    plan: z.string().nullable(),
    seats: z.number().int().nullable(),
    current_period_end: z.string().datetime().nullable(),
    has_license: z.boolean(),
    // In-app subscriptions (2026-06-19): the billing platform + the management route hint.
    platform: LicensePlatform,
    manage: LicenseManage,
});
// Account user admin (POST /api/account/users, PATCH …/role|active)
export const InviteUserRequest = z.object({ email: z.string().email(), full_name: z.string().optional(), role: Role });
export const AccountUser = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    full_name: z.string(),
    role: Role.nullable(),
    is_active: z.boolean(),
    activated: z.boolean(),
});
// Account self-deletion (POST /api/account/delete) — App Store + Google Play mandate in-app
// account+data deletion. Owner-only, password re-auth. Allowlisted so a locked account can still
// delete itself. Store subscriptions are cancelled by the user in the store, not here.
export const DeleteAccountRequest = z.object({ password: z.string() });
export const DeleteAccountResponse = z.object({ deleted: z.boolean() });
// Per-account comms config (PUT /api/account/comms/identities, …/:id/secret)
export const MessagingIdentityInput = z.object({
    id: z.string().uuid().optional(),
    channel: z.enum(['sms', 'email']),
    provider: z.enum(['twilio', 'sendgrid']),
    from_number_or_address: z.string().min(1),
    config: z.unknown().optional(),
});
export const SetSecretRequest = z.object({ secret: z.string().min(1) });
export const SecretDescriptor = z.object({ purpose: z.string(), last4: z.string().nullable(), configured: z.boolean() });
// Historical import (POST /api/onboarding/import, /:id, /:id/approve)
export const ImportUploadFile = z.object({ filename: z.string(), mimeType: z.string(), contentBase64: z.string() });
export const ImportRequest = z.object({ files: z.array(ImportUploadFile).min(1) });
export const ImportStartResponse = z.object({
    importBatchId: z.string().uuid(),
    fileCount: z.number().int(),
    pending: z.number().int(),
    status: z.string(),
});
export const ImportApproveResponse = z.object({ confirmed: z.number().int(), skipped: z.number().int(), status: z.string() });
// --- Priority (master spec Appendix B) ---
export const Priority = z.enum(['low', 'medium', 'high', 'urgent']);
// ===========================================================================
// Supplier Pricing & Procurement Connectors (pricing-source spec §12/§13)
// ===========================================================================
export const ConnectorKind = z.enum(['distributor', 'pricing_service', 'cost_data', 'retail', 'invoice_derived', 'manual']);
export const ConnectorMechanism = z.enum(['punchout', 'edi', 'api', 'oauth', 'file_import', 'service_export', 'scrape', 'derived', 'manual']);
export const LinkMethod = z.enum(['oauth_redirect', 'api_key', 'punchout_provision', 'edi_partner_setup', 'file_upload', 'service_credentials', 'none']);
export const ConnectionStatus = z.enum(['unlinked', 'pending', 'linked', 'error', 'expired']);
export const PriceBasis = z.enum(['derived', 'net', 'list_plus_multiplier', 'market_reference', 'manual']);
export const CapabilityTier = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export const PricingConnector = z.object({
    id: z.string().uuid(),
    code: z.string(),
    display_name: z.string(),
    logo_url: z.string().nullable().optional(),
    kind: ConnectorKind,
    trades_supported: z.array(z.string()),
    mechanism: ConnectorMechanism,
    max_capability_tier: z.number().int().min(0).max(4),
    link_method: LinkMethod,
    status: z.enum(['available', 'beta', 'assisted_only', 'deprecated']),
});
export const CreateSupplierConnectionRequest = z.object({
    connector_id: z.string().uuid().optional(),
    connector_code: z.string().optional(),
    supplier_id: z.string().uuid().nullable().optional(),
    account_number: z.string().nullable().optional(),
    config: z.record(z.unknown()).nullable().optional(),
});
export const LinkConnectionRequest = z.object({
    accountNumber: z.string().nullable().optional(),
    branchId: z.string().nullable().optional(),
    authRef: z.string().nullable().optional(),
    capabilities: z.array(z.enum(['pricing', 'quoting', 'ordering'])).optional(),
    config: z.record(z.unknown()).optional(),
    // Raw credentials the backend VAULTS (AES-256-GCM) and never returns; e.g. { api_key } /
    // { punchout_shared_secret } / { edi_partner_id }. Never persisted or echoed in plaintext (§8).
    secrets: z.record(z.string()).optional(),
});
export const PriceImportLineInput = z.object({
    supplier_sku: z.string().nullable().optional(),
    mfr_part_number: z.string().nullable().optional(),
    upc: z.string().nullable().optional(),
    unspsc: z.string().nullable().optional(),
    list_price: z.number().nullable().optional(),
    multiplier: z.number().nullable().optional(),
    net_unit_cost: z.number().nullable().optional(),
    uom_id: z.string().uuid().nullable().optional(),
    price_basis: PriceBasis.optional(),
    name: z.string().nullable().optional(),
});
export const CreatePriceImportRequest = z.object({
    supplier_connection_id: z.string().uuid().nullable().optional(),
    source_kind: ConnectorKind.optional(),
    file_attachment_id: z.string().uuid().nullable().optional(),
    tier: z.number().int().min(0).max(4).optional(),
    effective_date: z.string().datetime().nullable().optional(),
    lines: z.array(PriceImportLineInput).min(1),
});
export const ResolvedCost = z.object({
    materialId: z.string().uuid(),
    net: z.number().nullable(),
    priceBasis: PriceBasis.nullable(),
    tier: z.number().int().nullable(),
    binding: z.boolean(),
    stale: z.boolean(),
    contributors: z.array(z.string().uuid()),
    supplierId: z.string().uuid().nullable(),
});
// ===========================================================================
// Punchlists & Subcontractor Assignment (tasks/punchlists spec §8/§9)
// ===========================================================================
export const PunchlistStatus = z.enum(['open', 'in_progress', 'complete', 'signed_off', 'void']);
export const PunchlistSource = z.enum(['suggested', 'manual', 'template']);
export const PunchlistItemStatus = z.enum(['open', 'in_progress', 'ready_for_review', 'verified', 'wont_do', 'cancelled']);
export const CreateSubcontractorRequest = z.object({
    name: z.string().min(1),
    contact_id: z.string().uuid().nullable().optional(),
    default_trade_id: z.string().uuid().nullable().optional(),
    license_number: z.string().nullable().optional(),
    insurance_expires_on: z.string().datetime().nullable().optional(),
    w9_on_file: z.boolean().optional(),
    is_1099: z.boolean().optional(),
});
export const AddPunchlistItemRequest = z.object({
    description: z.string().min(1),
    area_location: z.string().nullable().optional(),
    trade_id: z.string().uuid().nullable().optional(),
    priority: Priority.optional(),
    due_at: z.string().datetime().nullable().optional(),
    assigned_to: z.string().uuid().nullable().optional(),
    subcontractor_id: z.string().uuid().nullable().optional(),
});
export const WorkQueueEntry = z.object({
    kind: z.enum(['task', 'punchlist_item', 'schedule_event']),
    id: z.string().uuid(),
    title: z.string(),
    priority: Priority,
    due_at: z.string().datetime().nullable(),
    status: z.string(),
    score: z.number(),
    punchlist_id: z.string().uuid().optional(),
    job_id: z.string().uuid().nullable().optional(),
});
// ===========================================================================
// Estimate builder — requests + reference DTOs (mobile estimate-builder track)
// ===========================================================================
//
// CASING: camelCase, matching the existing EstimateDetail / EstimateLineItemDto
// billing surface above (the backend serializes these via domain/billing.ts). The
// builder mutation endpoints (POST /estimates, /lines, PATCH/DELETE /lines/:lineId,
// POST /discount) return an EstimateDetail. money/decimal fields are decimal-as-string.
export const EstimateContext = z.enum(['residential', 'commercial', 'government']);
export const CreateEstimateRequest = z.object({
    customerId: z.string().uuid(),
    jobId: z.string().uuid().optional(),
    context: EstimateContext.optional(),
    markupPct: z.number().optional(),
    taxRateId: z.string().uuid().nullable().optional(),
    validUntil: z.string().nullable().optional(),
});
export const LineType = z.enum(['material', 'labor', 'assembly', 'custom']);
export const AddLineItemRequest = z.object({
    lineType: LineType,
    description: z.string().optional(),
    quantity: z.number(),
    unitCost: z.number().optional(),
    unitPrice: z.number().optional(),
    materialId: z.string().uuid().optional(),
    laborRateId: z.string().uuid().optional(),
    sourceAssemblyId: z.string().uuid().optional(),
    section: z.string().nullable().optional(),
    isOptional: z.boolean().optional(),
});
export const UpdateLineItemRequest = z.object({
    description: z.string().optional(),
    quantity: z.number().optional(),
    unitCost: z.number().optional(),
    unitPrice: z.number().nullable().optional(),
    section: z.string().nullable().optional(),
    isOptional: z.boolean().optional(),
});
// Primary in-app discount: percent or flat amount, or null to clear (pricing order markup→discount→tax).
export const EstimateDiscount = z.object({
    type: z.enum(['amount', 'percent']),
    value: z.number(),
});
export const SetEstimateDiscountRequest = z.object({
    discount: EstimateDiscount.nullable(),
});
export const TaxRateDto = z.object({
    id: z.string().uuid(),
    name: z.string(),
    ratePct: z.string(), // decimal-as-string (Decimal(6,3))
    isActive: z.boolean(),
});
export const CustomerDto = z.object({
    id: z.string().uuid(),
    customerType: z.string(), // CustomerType (residential | commercial | government)
    displayName: z.string(),
    companyName: z.string().nullable(),
});
export const UnitOfMeasureDto = z.object({
    id: z.string().uuid(),
    code: z.string(),
    displayName: z.string(), // maps to unit_of_measure.name (global catalog)
});
// ===========================================================================
// Calendar / Schedule events & Tasks (mobile calendar/tasks track)
// ===========================================================================
//
// CASING: snake_case. Unlike the billing surface, the WO-22 calendar/task endpoints
// (calendarRouter.ts) return RAW Prisma rows (no serializer), so the wire JSON is
// snake_case with ISO date strings (Date → JSON) and Prisma enum string values. The
// DTOs below MATCH that real contract (verified against domain/scheduling.ts +
// domain/tasks.ts). WorkQueueEntry (already defined above) is likewise snake_case.
export const ScheduleEventType = z.enum([
    'site_visit',
    'work',
    'inspection',
    'proposal_due',
    'permit_followup',
    'noc_deadline',
    'service_call',
]);
export const ScheduleEventDto = z.object({
    id: z.string().uuid(),
    event_type: ScheduleEventType,
    title: z.string(),
    description: z.string().nullable(),
    starts_at: z.string().nullable(),
    ends_at: z.string().nullable(),
    all_day: z.boolean(),
    is_flexible: z.boolean(),
    priority: Priority,
    assigned_to: z.string().uuid().nullable(),
    job_id: z.string().uuid().nullable(),
    job_phase_id: z.string().uuid().nullable(),
    location_address_id: z.string().uuid().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const CreateScheduleEventRequest = z.object({
    event_type: ScheduleEventType,
    title: z.string().min(1),
    description: z.string().nullable().optional(),
    starts_at: z.string().nullable().optional(),
    ends_at: z.string().nullable().optional(),
    all_day: z.boolean().optional(),
    is_flexible: z.boolean().optional(),
    priority: Priority.optional(),
    assigned_to: z.string().uuid().nullable().optional(),
    job_id: z.string().uuid().nullable().optional(),
    job_phase_id: z.string().uuid().nullable().optional(),
    location_address_id: z.string().uuid().nullable().optional(),
    // Service-call anchor: when event_type is `service_call` and no job_id is given, this
    // customer makes the create also spin up a linked service-call Job (spec 1).
    customer_id: z.string().uuid().nullable().optional(),
});
export const UpdateScheduleEventRequest = z.object({
    event_type: ScheduleEventType.optional(),
    title: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    starts_at: z.string().nullable().optional(),
    ends_at: z.string().nullable().optional(),
    all_day: z.boolean().optional(),
    is_flexible: z.boolean().optional(),
    priority: Priority.optional(),
    assigned_to: z.string().uuid().nullable().optional(),
    job_id: z.string().uuid().nullable().optional(),
    job_phase_id: z.string().uuid().nullable().optional(),
    location_address_id: z.string().uuid().nullable().optional(),
});
export const TaskType = z.enum([
    'follow_up',
    'schedule_next_phase',
    'estimate_followup',
    'order_materials',
    'review_queue_item',
    'disambiguate_fact',
    'punchlist_item',
    'generic',
]);
export const TaskStatus = z.enum(['open', 'in_progress', 'done', 'cancelled']);
export const TaskDto = z.object({
    id: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable(),
    task_type: TaskType,
    status: TaskStatus,
    priority: Priority,
    due_at: z.string().nullable(),
    job_id: z.string().uuid().nullable(),
    job_phase_id: z.string().uuid().nullable(),
    estimate_id: z.string().uuid().nullable(),
    invoice_id: z.string().uuid().nullable(),
    subcontractor_id: z.string().uuid().nullable(),
    assigned_to: z.string().uuid().nullable(),
    is_system_generated: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const CreateTaskRequest = z.object({
    title: z.string().min(1),
    description: z.string().nullable().optional(),
    task_type: TaskType.optional(),
    priority: Priority.optional(),
    due_at: z.string().nullable().optional(),
    job_id: z.string().uuid().nullable().optional(),
    job_phase_id: z.string().uuid().nullable().optional(),
    estimate_id: z.string().uuid().nullable().optional(),
    invoice_id: z.string().uuid().nullable().optional(),
    subcontractor_id: z.string().uuid().nullable().optional(),
    assigned_to: z.string().uuid().nullable().optional(),
});
export const UpdateTaskRequest = z.object({
    title: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    task_type: TaskType.optional(),
    status: TaskStatus.optional(),
    priority: Priority.optional(),
    due_at: z.string().nullable().optional(),
    job_id: z.string().uuid().nullable().optional(),
    job_phase_id: z.string().uuid().nullable().optional(),
    estimate_id: z.string().uuid().nullable().optional(),
    invoice_id: z.string().uuid().nullable().optional(),
    subcontractor_id: z.string().uuid().nullable().optional(),
    assigned_to: z.string().uuid().nullable().optional(),
});
// ---------------------------------------------------------------------------
// WO-D4 — Predictive estimating (org trades, suggested services, add-service)
// ---------------------------------------------------------------------------
// One of the org's selected contracting trades (spec §3.1/§4.1).
export const OrgTradeDto = z.object({
    tradeId: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    isPrimary: z.boolean(),
});
// GET /api/org/trades and PUT /api/org/trades response.
export const OrgTradesDto = z.object({
    trades: z.array(OrgTradeDto),
});
// PUT /api/org/trades request — replace the org's trade selection (set semantics; first = primary).
export const SetOrgTradesRequest = z.object({
    tradeIds: z.array(z.string().uuid()),
});
// A ranked curated service for the org's trade(s) (GET /api/services/suggested, spec §4.2).
// `estimatedTotal` is a decimal-as-string indicative figure (labor at the trade default bill_rate
// + material reference costs) — real pricing happens at add-service.
export const SuggestedServiceDto = z.object({
    id: z.string().uuid(), // service_template id
    code: z.string(),
    name: z.string(),
    category: z.string().nullable(),
    tradeId: z.string().uuid(),
    tradeCode: z.string(),
    tradeName: z.string(),
    defaultRank: z.number().int(),
    estimatedHours: z.number(),
    estimatedTotal: z.string(), // decimal-as-string
});
export const SuggestedServicesResponse = z.object({
    services: z.array(SuggestedServiceDto),
});
// POST /api/estimates/:id/add-service request/response.
export const AddServiceRequest = z.object({
    serviceTemplateId: z.string().uuid(),
});
export const AddServiceResponse = z.object({
    estimate: EstimateDetail,
    assemblyId: z.string().uuid(),
    lineCount: z.number().int(),
});
// ---------------------------------------------------------------------------
// WO-D5 — Suggested materials (surfaces the existing recommendMaterials recommender
// so the estimate builder offers a "+ Add material" quick-pick, spec §4.4).
// GET /api/materials/suggested?limit= → { materials: SuggestedMaterialDto[] }.
// ---------------------------------------------------------------------------
// One ranked recommended material. `unitCost` is a decimal-as-string (preferred-supplier
// cost) or null when the org has no priced supplier for it yet.
export const SuggestedMaterialDto = z.object({
    materialId: z.string().uuid(),
    name: z.string(),
    usageCount: z.number().int(),
    hasPreferredSupplier: z.boolean(),
    unitCost: z.string().nullable(), // decimal-as-string or null
});
export const SuggestedMaterialsResponse = z.object({
    materials: z.array(SuggestedMaterialDto),
});
export const API_CONTRACT_VERSION = '0.9.0';
